/* eslint-disable react-refresh/only-export-components --
 * Este arquivo é, por definição, um módulo MISTO: componentes visuais e os
 * formatadores que andam com eles (fmtCent, valorPrincipal, pctMeta), usados
 * por 8 telas. Separar em dois arquivos só para agradar o Fast Refresh
 * espalharia o mesmo assunto em dois lugares; o custo aceito é o refresh
 * cheio deste módulo em desenvolvimento. */
/**
 * CRM v2 — peças compartilhadas das telas do Comercial (Leads/Funil/
 * Indicadores/Metas). Referência visual: docs/architecture/mockup-comercial.html
 * (layout/estrutura) com os tokens REAIS do sistema (red/#C8252B via token
 * `red`, Oswald via `font-display`, paper via `bg-paper`).
 */

import { useEffect, type ReactNode, type SelectHTMLAttributes } from 'react';

import { Badge, Button, HelpTip, Select, useToast } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/lib/format';
import { formatDate, formatDateTime, formatAgendamento } from '@/lib/format-date';
import { mensagemErroCronometro, useCronometroActions, useOrcamentoCronometro } from '@/lib/crm/crm-store';
import type { CrmCard } from '@/lib/crm/crm-types';

/** Centavos → "R$ 18.900" (sem centavos quando inteiros, como no mockup). */
export function fmtCent(v: number | null | undefined): string {
  if (v == null) return '—';
  const reais = v / 100;
  return formatCurrency(reais, {
    minFractionDigits: reais % 1 === 0 ? 0 : 2,
    maxFractionDigits: 2,
  });
}

/** Valor "adaptável" do card.
 *  🔴 META-POR-MODALIDADE (01/09/2026): quem decide o número é o SERVIDOR
 *  (`valor-que-conta.ts`), e a tela só formata. Antes a regra vivia aqui e no
 *  servidor ao mesmo tempo — e as duas discordavam: o total "/mês" do funil
 *  somava o software por fora da mensalidade da locação, que já o inclui.
 *  O13: proposta sem valor nenhum mostra o convite "Montar orçamento". */
export function valorPrincipal(
  card: Pick<CrmCard, 'valorAVistaCentavos' | 'valorPorMesCentavos'>,
): string {
  if (card.valorAVistaCentavos > 0) return fmtCent(card.valorAVistaCentavos);
  if (card.valorPorMesCentavos > 0) return `${fmtCent(card.valorPorMesCentavos)}/mês`;
  return 'Montar orçamento';
}

/** PF12: etiqueta curta do modelo pro card/lista (compra não rotula). */
export function modeloAquisicaoBadge(
  card: Pick<CrmCard, 'modeloAquisicao'>,
): 'Locação' | 'Financiamento' | null {
  if (card.modeloAquisicao === 'locacao') return 'Locação';
  if (card.modeloAquisicao === 'financiamento') return 'Financiamento';
  return null;
}

/**
 * Select de toolbar com LARGURA FIXA (rodada UIUX, audit §achado transversal):
 * o `<Select>` do design system embrulha o campo num `div.w-full` — dentro de
 * um flex-wrap, cada select virava uma LINHA inteira (filtros empilhados na
 * vertical). Este wrapper fixa a largura no nível do flex item, restaurando a
 * toolbar horizontal do mockup.
 */
export function FiltroSelect({
  largura = 'w-44',
  className,
  children,
  ...props
}: Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> & { largura?: string }) {
  return (
    <div className={cn('shrink-0', largura, className)}>
      {/* C-4 (AUDITORIA-COMERCIAL): a largura fixa corta a opção escolhida
          ("Responsável: To…"). O `title` mostra o valor inteiro no hover — o
          <select> nativo não trunca com reticências legíveis por conta. */}
      <Select size="sm" title={typeof props.value === 'string' ? props.value : undefined} {...props}>
        {children}
      </Select>
    </div>
  );
}

export function CrmPageHeader(props: { titulo: string; subtitulo: string; acoes?: ReactNode; ajuda?: string }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
      <div>
        <h1 className="font-display text-3xl md:text-4xl tracking-tight text-ink-1 dark:text-ink-dark-1">
          {props.titulo}
          {/* H1 (AJUDA-UX-QA): "?" de ajuda contextual da tela. */}
          {props.ajuda && <HelpTip texto={props.ajuda} lado="baixo" className="ml-2 align-middle" />}
        </h1>
        <p className="text-sm text-ink-3 dark:text-ink-dark-3 mt-1">{props.subtitulo}</p>
      </div>
      {props.acoes ? <div className="flex items-center gap-2">{props.acoes}</div> : null}
    </div>
  );
}

/**
 * FASE-B (maquete scr-funil): badge de tipo agora é o `Badge` canônico —
 * variant `info` pros tipos de serviço (o warning fica reservado pro estado
 * "N dias parado", que ocupa o mesmo slot no topo do card).
 */
export function TipoServicoBadge(props: { tipo: 'pontual' | 'recorrente' }) {
  return (
    <Badge variant="info" size="sm">
      {props.tipo === 'recorrente' ? 'Recorrente' : 'Pontual'}
    </Badge>
  );
}

/**
 * Drawer lateral direito (padrão do mockup aprovado): scrim + painel
 * deslizante de 520px com header/tabs/corpo/rodapé por conta do caller.
 */
export function CrmDrawer(props: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  testId?: string;
}) {
  const { open, onClose } = props;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <>
      <div
        aria-hidden
        onClick={onClose}
        className={cn(
          'fixed inset-0 bg-ink-1/40 dark:bg-black/60 z-40 transition-opacity duration-200',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none',
        )}
      />
      <div
        role="dialog"
        aria-modal="true"
        data-testid={props.testId ?? 'crm-drawer'}
        className={cn(
          'fixed top-0 right-0 h-full w-[min(540px,96vw)] z-50 bg-paper dark:bg-paper-dark flex flex-col shadow-2xl',
          'transition-transform duration-300 ease-out',
          // 🔴 MOBILE-COMPLETO (M3): fechada, a gaveta ficava só DESLOCADA para
          // fora da janela — e no celular o documento inteiro escorregava 352 px
          // para o lado atrás dela. Abaixo de `md` ela sai do layout (`hidden`);
          // no computador continua deslizando como sempre (regra: o computador
          // não muda). ❌ Nada de `overflow-x: hidden` no body como remendo.
          open ? 'translate-x-0' : 'translate-x-[102%] max-md:hidden',
        )}
      >
        {open ? props.children : null}
      </div>
    </>
  );
}

/** Barrinha de progresso realizado × alvo (padrão do mockup/Financeiro). */
/**
 * Barra de progresso de meta — a ÚNICA do Comercial.
 *
 * D (AUDITORIA-COMERCIAL · causa "três cores para a mesma coisa"): havia
 * verde, vermelho e ocre para progresso — e vermelho, na leitura comum,
 * significa ERRO. Progresso não é erro: **em andamento é sempre a mesma cor**
 * (ocre/warning, que já é a da casa) e só a meta BATIDA muda pra sucesso.
 * Quem quiser sinalizar atraso usa texto, não a cor da barra.
 */
export function BarraMeta(props: { pct: number | null; className?: string }) {
  const pct = props.pct == null ? 0 : Math.max(0, Math.min(100, props.pct));
  const ok = (props.pct ?? 0) >= 100;
  return (
    <div className={cn('h-2 rounded-pill bg-line-soft dark:bg-line-soft-dark overflow-hidden', props.className)}>
      <div
        className={cn('h-full rounded-pill transition-all duration-700', ok ? 'bg-success' : 'bg-warning')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

export function pctMeta(realizado: number, meta: number | null): number | null {
  if (meta == null || meta <= 0) return null;
  return Math.round((realizado / meta) * 100);
}

/**
 * Timeline única (card e lead) — estilo do mockup aprovado.
 * CA-5: quando o caller passa `onConcluir`/`onReabrir`, cada tarefa aberta
 * ganha o botão "Concluir" e a concluída ganha o ✓ + "Desfazer" discreto.
 */
export function Timeline({
  atividades,
  onConcluir,
  onReabrir,
  mutandoId,
}: {
  atividades: {
    id: string;
    data: string;
    autorNome: string | null;
    descricao: string;
    tipoNome: string | null;
    isTarefa: boolean;
    dataAgendada: string | null;
    concluidaEm: string | null;
    eventoSistema: string | null;
    /** PF0-d: responsável DA ATIVIDADE (nome resolvido; nunca id cru). */
    atribuidoNome?: string | null;
  }[];
  /** CA-5: conclui a tarefa (só tarefas abertas mostram o botão). */
  onConcluir?: (atvId: string) => void;
  /** CA-5: desfaz a conclusão (espelho do concluir). */
  onReabrir?: (atvId: string) => void;
  /** id da tarefa com mutação em voo (desabilita o botão dela). */
  mutandoId?: string | null;
}) {
  if (atividades.length === 0) {
    return <p className="text-sm text-ink-3 dark:text-ink-dark-3">Nenhuma atividade ainda.</p>;
  }
  return (
    <div className="relative pl-5 before:content-[''] before:absolute before:left-1.5 before:top-1 before:bottom-1 before:w-0.5 before:bg-line dark:before:bg-line-dark">
      {atividades.map((a) => (
        <div key={a.id} className="relative mb-4">
          <span className="absolute -left-[17px] top-1 w-2.5 h-2.5 rounded-full bg-paper-surface dark:bg-paper-dark-surface border-2 border-red" />
          <p className="text-xs font-semibold text-ink-3 dark:text-ink-dark-3">
            {formatDate(a.data)}
            {a.autorNome ? ` — ${a.autorNome}` : ''}
          </p>
          <p className="text-sm text-ink-1 dark:text-ink-dark-1 mt-0.5">
            {a.isTarefa ? (
              <>
                <b>Tarefa:</b>{' '}
                <span className={cn(a.concluidaEm && 'line-through text-ink-3 dark:text-ink-dark-3')}>
                  {a.descricao}
                </span>
                {a.dataAgendada ? ` (${formatAgendamento(a.dataAgendada)})` : ''}
                {a.atribuidoNome ? ` · responsável: ${a.atribuidoNome}` : ''}
                {a.concluidaEm ? (
                  <>
                    {' '}
                    <span className="text-success font-bold" title="Tarefa concluída">
                      ✓ concluída
                    </span>
                    {onReabrir && (
                      <button
                        type="button"
                        data-testid={`tarefa-reabrir-${a.id}`}
                        disabled={mutandoId === a.id}
                        onClick={() => onReabrir(a.id)}
                        className="ml-2 text-xs font-semibold text-ink-3 dark:text-ink-dark-3 underline hover:text-red disabled:opacity-50"
                      >
                        Desfazer
                      </button>
                    )}
                  </>
                ) : (
                  onConcluir && (
                    <button
                      type="button"
                      data-testid={`tarefa-concluir-${a.id}`}
                      disabled={mutandoId === a.id}
                      onClick={() => onConcluir(a.id)}
                      className="ml-2 inline-flex items-center gap-1 rounded-pill border border-line dark:border-line-dark px-2 py-0.5 text-xs font-bold text-ink-2 dark:text-ink-dark-2 hover:border-success hover:text-success disabled:opacity-50"
                    >
                      Concluir
                    </button>
                  )
                )}
              </>
            ) : a.tipoNome ? (
              <>
                <b>{a.tipoNome}:</b> {a.descricao}
              </>
            ) : (
              a.descricao
            )}
          </p>
        </div>
      ))}
    </div>
  );
}

/**
 * CA-10: botão de 3 estados do orçamentista — UM slot, o estado vem do
 * backend (GET orcamento-cronometro). Vive na aba Orçamento do drawer do
 * card e na fila da Home do orçamentista.
 *
 *   fazer_orcamento       → "Fazer orçamento" (inicia a produção)
 *   producao_em_andamento → aviso "Produzindo desde X" (a parada é mover
 *                           o card pra conferência — sem botão de parar)
 *   iniciar_revisao       → "Iniciar revisão" (fecha a fila, abre o trabalho)
 *   revisao_em_andamento  → "Concluir revisão" (avisa o vendedor no sininho)
 *   nenhum                → slot vazio
 *
 * R3 (RESUMO-REGIME): o botão é ferramenta de quem PRODUZ o orçamento — só
 * admin, super-admin e quem é do departamento 'orcamentista' veem o slot.
 * Vendedor não vê (o cronômetro não é dele).
 */
export function BotaoCronometroOrcamento({
  cardId,
  aoIniciarProducao,
}: {
  cardId: string;
  /** Chamado após "Fazer orçamento" gravar — o caller decide pra onde navegar. */
  aoIniciarProducao?: () => void;
}) {
  const { user } = useAuth();
  const { data: cron } = useOrcamentoCronometro(cardId);
  const acoes = useCronometroActions();
  const toast = useToast();

  const podeVer =
    !!user && (user.isAdmin || user.isSuperAdmin || user.departments.includes('orcamentista'));
  if (!podeVer) return null;
  if (!cron || cron.botao === 'nenhum') return null;

  if (cron.botao === 'producao_em_andamento') {
    return (
      <span
        data-testid="cronometro-produzindo"
        className="inline-flex items-center gap-1.5 rounded-pill bg-warning/10 text-warning px-2.5 py-1 text-xs font-bold"
      >
        Produzindo desde {cron.producaoIniciadoEm ? formatDateTime(cron.producaoIniciadoEm) : '—'}
        <HelpTip texto="O tempo de produção está contando. Não tem botão de parar: a contagem encerra sozinha quando a proposta vai pra fase de conferência." />
      </span>
    );
  }

  if (cron.botao === 'fazer_orcamento') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Button
          size="sm"
          data-testid="cronometro-fazer-orcamento"
          loading={acoes.iniciarProducao.isPending}
          onClick={async () => {
            try {
              await acoes.iniciarProducao.mutateAsync(cardId);
              toast.show('Produção iniciada — o tempo está contando.');
              aoIniciarProducao?.();
            } catch (err) {
              toast.show(mensagemErroCronometro(err, 'Não foi possível iniciar a produção.'), {
                variant: 'error',
              });
            }
          }}
        >
          Fazer orçamento
        </Button>
        <HelpTip texto="Clique ao começar a montar o orçamento — o tempo de produção passa a contar. A parada é automática quando a proposta vai pra conferência." />
      </span>
    );
  }

  if (cron.botao === 'iniciar_revisao') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <Button
          size="sm"
          variant="secondary"
          data-testid="cronometro-iniciar-revisao"
          loading={acoes.iniciarRevisao.isPending}
          onClick={async () => {
            try {
              await acoes.iniciarRevisao.mutateAsync(cardId);
              toast.show('Revisão iniciada — o tempo de espera na fila parou de contar.');
            } catch (err) {
              toast.show(mensagemErroCronometro(err, 'Não foi possível iniciar a revisão.'), {
                variant: 'error',
              });
            }
          }}
        >
          Iniciar revisão
        </Button>
        <HelpTip texto="A proposta voltou pra fase de orçamento: é um pedido de revisão. Clique ao começar a revisar — o tempo de espera na fila é medido até aqui." />
      </span>
    );
  }

  // revisao_em_andamento
  return (
    <span className="inline-flex items-center gap-1.5">
      <Button
        size="sm"
        data-testid="cronometro-concluir-revisao"
        loading={acoes.concluirRevisao.isPending}
        onClick={async () => {
          try {
            await acoes.concluirRevisao.mutateAsync(cardId);
            toast.show('Revisão concluída — o vendedor foi avisado no sininho.');
          } catch (err) {
            toast.show(mensagemErroCronometro(err, 'Não foi possível concluir a revisão.'), {
              variant: 'error',
            });
          }
        }}
      >
        Concluir revisão
      </Button>
      <HelpTip texto="Encerra a revisão e avisa o vendedor da proposta no sininho. Publicar uma versão nova (Rev N) também conclui a revisão automaticamente." />
    </span>
  );
}

/** Pill de status/fase com a cor configurada (tabela `fases`/`lead_status`). */
/**
 * FASE-C → LEAD-TELA: guarda de contraste pra cor de STATUS vinda do banco —
 * mesmo racional do `corTagLegivel` do Funil (a cor é DADO configurável; um
 * tom claro reprovava AA sobre o card branco). Escurece só na exibição até o
 * texto passar de 4.5:1 sobre branco; o dado fica intacto. Morava na
 * LeadsPage; subiu pra cá quando a tela do lead passou a precisar dela.
 */
export function corStatusLegivel(hexCor: string): string {
  const hex = hexCor.replace('#', '');
  if (hex.length !== 6) return hexCor;
  let r = parseInt(hex.slice(0, 2), 16);
  let g = parseInt(hex.slice(2, 4), 16);
  let b = parseInt(hex.slice(4, 6), 16);
  const lum = (x: number) => {
    const v = x / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const contrasteComBranco = () => 1.05 / (0.2126 * lum(r) + 0.7152 * lum(g) + 0.0722 * lum(b) + 0.05);
  for (let i = 0; i < 12 && contrasteComBranco() < 4.5; i++) {
    r = Math.floor(r * 0.82);
    g = Math.floor(g * 0.82);
    b = Math.floor(b * 0.82);
  }
  const h = (x: number) => x.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

export function CorPill(props: { nome: string; cor: string }) {
  return (
    <span
      className="inline-flex px-2.5 py-0.5 rounded-pill text-xs font-bold"
      style={{ backgroundColor: `${props.cor}1F`, color: props.cor }}
    >
      {props.nome}
    </span>
  );
}
